<template>
  <div class="message-input-root flex flex-col gap-1.5">
    <ReplyPreview v-if="replyToMessage" :message="replyToMessage" @close="$emit('cancel-reply')" />

    <div class="message-format-row flex items-center justify-between">
      <FormatToolbar
        :message-text="messageText"
        :active-formats="activeFormats"
        @format-bold="formatBold"
        @format-italic="formatItalic"
        @format-strikethrough="formatStrikethrough"
        @format-monospace="formatMonospace"
      />

      <div class="message-header-actions flex items-center gap-1">
        <Button
          text
          rounded
          size="normal"
          class="composer-action flex-shrink-0 text-[var(--p-surface-500)] p-0.5!"
          :aria-label="
            shortcutLabel(
              t('whatsapp.actions.expand_editor'),
              MESSAGE_INPUT_SHORTCUTS.expandEditor.label,
            )
          "
          :title="
            shortcutLabel(
              t('whatsapp.actions.expand_editor'),
              MESSAGE_INPUT_SHORTCUTS.expandEditor.label,
            )
          "
          @click="toggleExpandedEditor"
          v-tooltip.top="
            shortcutLabel(
              t('whatsapp.actions.expand_editor'),
              MESSAGE_INPUT_SHORTCUTS.expandEditor.label,
            )
          "
        >
          <template #icon><Expand :size="16" /></template>
        </Button>

        <TextAIAssistant
          ref="aiAssistantRef"
          class="composer-action"
          :model-value="messageText"
          field-type="textarea"
          :title="
            shortcutLabel(t('whatsapp.actions.ia_asist'), MESSAGE_INPUT_SHORTCUTS.aiAssistant.label)
          "
          v-tooltip.top="
            shortcutLabel(t('whatsapp.actions.ia_asist'), MESSAGE_INPUT_SHORTCUTS.aiAssistant.label)
          "
          panel-mode="fixed"
          :embedded="false"
          @replace="handleAIReplace"
          @append="handleAIAppend"
        />

        <Button
          text
          rounded
          size="normal"
          class="composer-action flex-shrink-0 text-[var(--p-surface-500)] p-0.5!"
          :aria-label="
            shortcutLabel(t('whatsapp.actions.finish_chat'), MESSAGE_INPUT_SHORTCUTS.endChat.label)
          "
          :title="
            shortcutLabel(t('whatsapp.actions.finish_chat'), MESSAGE_INPUT_SHORTCUTS.endChat.label)
          "
          @click="emit('end-chat')"
          v-tooltip.top="
            shortcutLabel(t('whatsapp.actions.finish_chat'), MESSAGE_INPUT_SHORTCUTS.endChat.label)
          "
        >
          <template #icon>
            <IconHand width="18" height="18" class="text-[var(--p-primary-color)]" />
          </template>
        </Button>
      </div>
    </div>

    <div class="flex min-w-0 items-center gap-2.5">
      <div
        class="message-composer-surface relative flex min-w-0 flex-1 items-center px-1.5 py-1  border border-gray-200 dark:border-neutral-700 focus-within:border-[var(--p-primary-color)] focus-within:ring-2 focus-within:ring-[var(--p-primary-color)]/10 bg-gray-50/50 dark:bg-neutral-900/50 rounded-xl transition-all duration-200"
      >
        <ShortcutSuggestion
          v-if="matchingShortcuts.length"
          :shortcuts="matchingShortcuts"
          :active-index="activeShortcutIndex"
          @select="applySlashShortcut"
        />

        <Button
          ref="emojiButtonRef"
          text
          rounded
          size="normal"
          class="composer-action flex-shrink-0"
          :aria-label="
            shortcutLabel(t('whatsapp.actions.select_emojis'), MESSAGE_INPUT_SHORTCUTS.emojis.label)
          "
          :title="
            shortcutLabel(t('whatsapp.actions.select_emojis'), MESSAGE_INPUT_SHORTCUTS.emojis.label)
          "
          @click.stop="toggleEmojiPicker"
          v-tooltip.top="
            shortcutLabel(t('whatsapp.actions.select_emojis'), MESSAGE_INPUT_SHORTCUTS.emojis.label)
          "
        >
          <template #icon>
            <IconEmoji width="20" height="20" class="text-[var(--p-primary-color)]" />
          </template>
        </Button>
        <EmojiPicker ref="emojiPanel" hide-trigger position="top" @emoji="handleEmojiSelect" />

        <div
          ref="messageInput"
          contenteditable="true"
          role="textbox"
          aria-multiline="true"
          :data-placeholder="t('whatsapp.message_input.placeholder')"
          class="rich-message-editor compact items-center min-w-0 flex-1 bg-transparent border-0 outline-none text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)] text-sm px-2 py-0.5 max-h-[9rem] overflow-y-auto leading-tight whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
          @keydown="handleKeydown"
          @input="handleEditorInput"
          @paste="handlePaste"
          @keyup="updateActiveFormats"
          @mouseup="updateActiveFormats"
        ></div>

        <Button
          v-if="messageText.trim()"
          text
          rounded
          size="normal"
          class="composer-action flex-shrink-0 text-[var(--p-primary-color)] bg-[var(--p-primary-100)]! dark:bg-[var(--p-primary-900)]! hover:bg-[var(--p-primary-200)]! dark:hover:bg-[var(--p-primary-700)]! p-0.5!"
          @click="$emit('send-message')"
          v-tooltip.top="$t('whatsapp.actions.send_message')"
        >
          <template #icon>
            <IconSend width="18" height="18" class="text-[var(--p-primary-color)]" />
          </template>
        </Button>
        <Button
          v-else
          text
          rounded
          size="normal"
          class="composer-action flex-shrink-0 text-[var(--p-primary-color)] p-0.5!"
          @click="$emit('start-audio-recording')"
          v-tooltip.top="$t('whatsapp.actions.audio_message')"
        >
          <template #icon>
            <IconMicrophone width="18" height="18" class="text-[var(--p-primary-color)]" />
          </template>
        </Button>
      </div>
    </div>

    <div
      class="message-secondary-actions flex flex-wrap items-center justify-between gap-2 px-1 sm:px-2"
    >
      <div class="flex min-w-0 flex-1 flex-wrap items-center justify-start gap-1.5 sm:gap-3">
        <Button
          ref="fileUploadButtonRef"
          text
          rounded
          size="small"
          class="p-0.5!"
          :aria-label="
            shortcutLabel(
              t('whatsapp.actions.attach_files'),
              MESSAGE_INPUT_SHORTCUTS.attachFiles.label,
            )
          "
          :title="
            shortcutLabel(
              t('whatsapp.actions.attach_files'),
              MESSAGE_INPUT_SHORTCUTS.attachFiles.label,
            )
          "
          @click="toggleFileUpload"
          v-tooltip.top="
            shortcutLabel(
              t('whatsapp.actions.attach_files'),
              MESSAGE_INPUT_SHORTCUTS.attachFiles.label,
            )
          "
        >
          <template #icon>
            <IconPlus width="16" height="16" class="text-[var(--p-primary-color)]" />
          </template>
        </Button>
        <Popover ref="fileUploadPanel" @show="focusFirstPopoverControl(fileUploadPanel)">
          <FileUploadMenu @files-select="handleFilesSelect" @close="closeFileUpload" />
        </Popover>

        <Button
          ref="quickRepliesButtonRef"
          text
          rounded
          size="small"
          class="flex-shrink-0 text-[var(--p-surface-500)] p-0.5!"
          :aria-label="
            shortcutLabel(
              t('whatsapp.actions.fast_responses'),
              MESSAGE_INPUT_SHORTCUTS.quickReplies.label,
            )
          "
          :title="
            shortcutLabel(
              t('whatsapp.actions.fast_responses'),
              MESSAGE_INPUT_SHORTCUTS.quickReplies.label,
            )
          "
          @click="toggleQuickReplies"
          v-tooltip.top="
            shortcutLabel(
              t('whatsapp.actions.fast_responses'),
              MESSAGE_INPUT_SHORTCUTS.quickReplies.label,
            )
          "
        >
          <template #icon>
            <IconStair width="18" height="18" class="text-[var(--p-primary-color)]" />
          </template>
        </Button>

        <Button
          text
          rounded
          size="small"
          :severity="isSignatureEnabled ? 'primary' : 'secondary'"
          class="p-0.5! hover:bg-[var(--p-primary-50)]! dark:hover:bg-[var(--p-primary-900)]/30!"
          :aria-label="
            shortcutLabel(t('whatsapp.actions.signature'), MESSAGE_INPUT_SHORTCUTS.signature.label)
          "
          :title="
            shortcutLabel(t('whatsapp.actions.signature'), MESSAGE_INPUT_SHORTCUTS.signature.label)
          "
          @click="emit('toggle-signature')"
          v-tooltip.top="
            shortcutLabel(t('whatsapp.actions.signature'), MESSAGE_INPUT_SHORTCUTS.signature.label)
          "
        >
          <template #icon>
            <IconSignature width="18" height="18" class="text-[var(--p-primary-color)]" />
          </template>
        </Button>

        <span v-tooltip.top="translationTooltip" class="inline-flex">
          <Button
            text
            rounded
            size="small"
            :severity="isTranslationEnabled ? 'primary' : 'secondary'"
            :disabled="isTranslationDisabled"
            :aria-label="translationShortcutLabel"
            :title="translationShortcutLabel"
            :class="[
              'p-0.5! transition-all duration-200 hover:bg-[var(--p-primary-50)]! dark:hover:bg-[var(--p-primary-900)]/30!',
              isTranslationEnabled
                ? 'bg-[var(--p-primary-50)]! text-[var(--p-primary-color)]! ring-1 ring-[var(--p-primary-color)]/35 shadow-sm dark:bg-[var(--p-primary-900)]/30!'
                : 'text-[var(--p-surface-500)]!',
            ]"
            @click="toggleTranslation"
          >
            <template #icon>
              <IconLanguage
                stroke-width="2"
                width="18"
                height="18"
                :class="
                  isTranslationEnabled
                    ? 'text-[var(--p-primary-color)]'
                    : 'text-[var(--p-surface-500)]'
                "
              />
            </template>
          </Button>
        </span>

        <div
          v-if="isTranslationEnabled && !isTranslationDisabled"
          class="flex items-center gap-1.5"
        >
          <span class="hidden text-xs text-[var(--p-surface-500)] sm:inline">
            {{ t('whatsapp.message_input.agent_language') }}
          </span>
          <AppSelect
            :model-value="agentLanguage"
            :options="translationLanguageOptions"
            option-label="label"
            option-value="value"
            :show-clear="false"
            size="small"
            class="w-30"
            @update:model-value="emit('update:agent-language', $event)"
          />
          <span class="hidden text-xs text-[var(--p-surface-500)] sm:inline">
            {{ t('whatsapp.message_input.client_language') }}
          </span>
          <AppSelect
            :model-value="clientLanguage"
            :options="translationLanguageOptions"
            option-label="label"
            option-value="value"
            :show-clear="false"
            size="small"
            class="w-30"
            @update:model-value="emit('update:client-language', $event)"
          />
        </div>
      </div>

      <div class="ml-auto flex shrink-0 items-center justify-end">
        <slot name="actions-right" />
      </div>
    </div>

    <div
      v-if="isSignatureEnabled"
      class="message-signature-hint flex items-center gap-2 px-4 py-2 bg-[var(--p-primary-50)] dark:bg-[var(--p-primary-900)]/20 border-l-2 border-[var(--p-primary-color)] rounded text-xs"
    >
      <IconSignature width="14" height="14" class="text-[var(--p-primary-color)] flex-shrink-0" />
      <span class="text-[var(--p-surface-600)] dark:text-[var(--p-surface-400)]">
        {{ t('whatsapp.message_input.messages_will_include') }}:
        <span class="font-medium italic">> {{ userName }}</span>
      </span>
    </div>
  </div>

  <Popover
    ref="quickRepliesPanel"
    :dismissable="false"
    @show="focusFirstPopoverControl(quickRepliesPanel)"
  >
    <QuickReplies @select-reply="handleQuickReply" />
  </Popover>

  <ExpandedMessageInput
    ref="expandedMessageInputRef"
    v-model:visible="isExpandedModalVisible"
    v-model:message-text="messageText"
    :is-signature-enabled="isSignatureEnabled"
    :user-name="userName"
    :reply-to-message="replyToMessage"
    @send-message="$emit('send-message')"
    @toggle-signature="$emit('toggle-signature')"
    @files-select="handleFilesSelect"
    @cancel-reply="$emit('cancel-reply')"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import Select from 'primevue/select'
import { Expand } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useRichWhatsappEditor } from '@/components/whatsapp/composables/useRichWhatsappEditor'
import { extractImageFilesFromClipboard } from '@/components/whatsapp/composables/useClipboardImages'
import type { FilePreviewCategory } from '@/components/whatsapp/composables/useFiles'
import { useChatStore } from '@/components/whatsapp/stores/useChatStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useShortcutStore } from '@/stores/useShortcutStore'
import type { Message } from '../../types/interfaces'
import type { MessageShortcutDto } from '@/services/shortcut/interfaces'
import FormatToolbar from './FormatToolbar.vue'
import EmojiPicker from '@/components/atoms/emoji/EmojiPicker.vue'
import FileUploadMenu from '../../molecules/file-upload/FileUploadMenu.vue'
import QuickReplies from '../../molecules/quick-replies/QuickReplies.vue'
import ReplyPreview from '../../molecules/message/ReplyPreview.vue'
import ExpandedMessageInput from './ExpandedMessageInput.vue'
import ShortcutSuggestion from '../../molecules/shortcut/ShortcutSuggestion.vue'
import TextAIAssistant from '@/components/organism/textAiAssistant/TextAIAssistant.vue'
import IconSignature from '@/assets/svg/signature_icon.svg?component'
import IconLanguage from '@/assets/svg/languaje-icon.svg?component'
import IconPlus from '@/assets/svg/plus_icon.svg?component'
import IconEmoji from '@/assets/svg/emoji_icon.svg?component'
import IconStair from '@/assets/svg/stair_icon.svg?component'
import IconHand from '@/assets/svg/hand_wave_icon.svg?component'
import IconMicrophone from '@/assets/svg/microphone_icon.svg?component'
import IconSend from '@/assets/svg/send_icon.svg?component'
import { TRANSLATION_LANGUAGE_OPTIONS } from '@/constants/translation-languages'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'

interface Props {
  isSignatureEnabled: boolean
  isTranslationEnabled?: boolean
  isTranslationDisabled?: boolean
  agentLanguage?: string
  clientLanguage?: string
  userName: string
  replyToMessage?: Message | null
  messageText?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'send-message': []
  'start-audio-recording': []
  'toggle-signature': []
  'toggle-translation': []
  'update:agent-language': [language: string]
  'update:client-language': [language: string]
  'files-select': [files: File[], category?: FilePreviewCategory]
  'cancel-reply': []
  'end-chat': []
  'transfer-chat': []
  'update:message-text': [text: string]
}>()

const MESSAGE_INPUT_SHORTCUTS = {
  emojis: { key: 'e', label: 'Alt+Shift+E' },
  attachFiles: { key: 'a', label: 'Alt+Shift+A' },
  quickReplies: { key: 'r', label: 'Alt+Shift+R' },
  signature: { key: 'f', label: 'Alt+Shift+F' },
  translation: { key: 's', label: 'Alt+Shift+S' },
  transferChat: { key: 't', label: 'Alt+Shift+T' },
  expandEditor: { key: 'x', label: 'Alt+Shift+X' },
  aiAssistant: { key: 'h', label: 'Alt+Shift+H' },
  endChat: { key: 'p', label: 'Alt+Shift+P' },
} as const

type TextAIAssistantExpose = {
  toggleAssistant: () => void
}

type ExpandedMessageInputExpose = {
  toggleEmojiPickerShortcut: (event: Event) => void
  toggleFileUploadShortcut: (event: Event) => void
  toggleQuickRepliesShortcut: (event: Event) => void
  toggleAIMenuShortcut: (event: Event) => void
}

const shortcutLabel = (label: string, shortcut: string) => `${label} (${shortcut})`

// Arranca con el borrador que llega en la prop: el watcher de abajo emite el
// valor inicial hacia el padre, y partir de '' borraria lo que habia guardado.
const messageText = ref(props.messageText ?? '')
const {
  editorRef: messageInput,
  activeFormats,
  syncEditorFromText,
  syncTextFromEditor,
  focusEditor,
  toggleFormat,
  updateActiveFormats,
  insertText,
  handlePaste: handleEditorPaste,
  handleFormatKeydown,
  handleUndo,
  handleRedo,
  recordUndoState,
} = useRichWhatsappEditor(messageText)

const formatBold = () => toggleFormat('bold')
const formatItalic = () => toggleFormat('italic')
const formatStrikethrough = () => toggleFormat('strikethrough')
const formatMonospace = () => toggleFormat('monospace')

const chatStore = useChatStore()
const authStore = useAuthStore()
const shortcutStore = useShortcutStore()
const { t } = useI18n()

const emojiPanel = ref()
const quickRepliesPanel = ref()
const fileUploadPanel = ref()
const emojiButtonRef = ref()
const fileUploadButtonRef = ref()
const quickRepliesButtonRef = ref()
const aiAssistantRef = ref<TextAIAssistantExpose | null>(null)
const expandedMessageInputRef = ref<ExpandedMessageInputExpose | null>(null)
const isExpandedModalVisible = ref(false)
const activeShortcutIndex = ref(0)
const slashShortcutCommand = computed(() => {
  const match = messageText.value.match(/(^|\s)\/([^\s]*)$/)
  if (!match || match.index === undefined) return null

  const leadingText = match[1] ?? ''
  const query = match[2] ?? ''
  const commandStart = match.index + leadingText.length

  return {
    query,
    start: commandStart,
    end: messageText.value.length,
  }
})
const translationTooltip = computed(() =>
  props.isTranslationDisabled
    ? t('whatsapp.message_input.ai_disabled_for_campaign')
    : shortcutLabel(t('whatsapp.actions.traduction'), MESSAGE_INPUT_SHORTCUTS.translation.label),
)
const translationShortcutLabel = computed(() =>
  shortcutLabel(t('whatsapp.actions.traduction'), MESSAGE_INPUT_SHORTCUTS.translation.label),
)
const translationLanguageOptions = computed(() =>
  TRANSLATION_LANGUAGE_OPTIONS.map((option) => ({
    label: t(option.labelKey),
    value: option.value,
  })),
)

watch(
  () => props.messageText,
  (externalMessageText) => {
    if (externalMessageText !== undefined && externalMessageText !== messageText.value) {
      messageText.value = externalMessageText
      // Cambio de chat: el editor toma el borrador del nuevo y se ajusta a su
      // alto, que no tiene por que ser el del anterior.
      nextTick(() => {
        syncEditorFromText()
        autoResizeTextarea()
      })
    }
  },
)

// Cargar shortcuts cuando cambia la campaña del chat seleccionado
watch(
  () => chatStore.selectedChat?.campaign_id,
  (campaignId) => {
    if (campaignId) {
      shortcutStore.loadShortcuts(campaignId, authStore.userId ?? undefined)
    }
  },
  { immediate: true },
)

const matchingShortcuts = computed<MessageShortcutDto[]>(() => {
  const command = slashShortcutCommand.value
  if (!command) return []

  const query = command.query.trim().toLowerCase()
  return shortcutStore.shortcuts.filter((shortcut) => {
    const name = shortcut.name?.toLowerCase() ?? ''
    return !query || name.includes(query)
  })
})

// Resetear índice activo cuando cambia la lista
watch(matchingShortcuts, () => {
  activeShortcutIndex.value = 0
})

watch(
  messageText,
  (text) => {
    emit('update:message-text', text)
    nextTick(syncEditorFromText)
  },
  { immediate: true },
)

const applyShortcut = (shortcut: MessageShortcutDto) => {
  recordUndoState()
  messageText.value = shortcut.description
  activeShortcutIndex.value = 0
  nextTick(() => {
    syncEditorFromText()
    autoResizeTextarea()
    focusEditor()
  })
}

const applySlashShortcut = (shortcut: MessageShortcutDto) => {
  const command = slashShortcutCommand.value
  if (!command) {
    applyShortcut(shortcut)
    return
  }

  recordUndoState()
  messageText.value =
    messageText.value.slice(0, command.start) +
    shortcut.description +
    messageText.value.slice(command.end)
  activeShortcutIndex.value = 0
  nextTick(() => {
    syncEditorFromText()
    autoResizeTextarea()
    focusEditor()
  })
}

const insertLineBreak = () => {
  insertText('\n')
  nextTick(() => {
    autoResizeTextarea()
    const editor = messageInput.value
    if (editor) {
      editor.scrollTop = editor.scrollHeight
    }
  })
}

const handleKeydown = (event: KeyboardEvent) => {
  if (handleUndo(event)) return
  if (handleRedo(event)) return
  if (handleFormatKeydown(event)) return

  if (event.key === 'Enter' && (event.shiftKey || event.ctrlKey)) {
    event.preventDefault()
    insertLineBreak()
    return
  }

  if (matchingShortcuts.value.length) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      activeShortcutIndex.value = (activeShortcutIndex.value + 1) % matchingShortcuts.value.length
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      activeShortcutIndex.value =
        (activeShortcutIndex.value - 1 + matchingShortcuts.value.length) %
        matchingShortcuts.value.length
      return
    }
    if (event.key === 'Tab' || (event.key === 'Enter' && !event.shiftKey)) {
      event.preventDefault()
      applySlashShortcut(matchingShortcuts.value[activeShortcutIndex.value])
      return
    }
    if (event.key === 'Escape') {
      const command = slashShortcutCommand.value
      if (command) {
        recordUndoState()
        messageText.value = messageText.value.slice(0, command.start)
      }
      nextTick(syncEditorFromText)
      return
    }
  }

  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    emit('send-message')
  }
}

const getComponentElement = (componentRef: unknown) => {
  if (componentRef instanceof HTMLElement) return componentRef
  if (
    componentRef &&
    typeof componentRef === 'object' &&
    '$el' in componentRef &&
    componentRef.$el instanceof HTMLElement
  ) {
    return componentRef.$el
  }
  return undefined
}

const createAnchoredPopoverEvent = (event: Event, target?: HTMLElement) => {
  if (!target) return event

  return {
    ...event,
    currentTarget: target,
    target,
  } as unknown as Event
}

const getPopoverContainer = (popoverRef: unknown) => {
  const candidate =
    popoverRef && typeof popoverRef === 'object' && 'value' in popoverRef
      ? popoverRef.value
      : popoverRef

  if (
    candidate &&
    typeof candidate === 'object' &&
    'container' in candidate &&
    candidate.container instanceof HTMLElement
  ) {
    return candidate.container
  }

  return getComponentElement(candidate)
}

const focusFirstPopoverControl = (popoverRef: unknown) => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const popover = getPopoverContainer(popoverRef)
      const focusable = popover?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )

      focusable?.focus()
    })
  })
}

const isInsideElement = (target: EventTarget | null, element?: HTMLElement) =>
  Boolean(target instanceof Node && element?.contains(target))

const isQuickRepliesShortcutDialogOpen = () =>
  Boolean(document.querySelector('.quick-replies-shortcut-dialog'))

const isInsideDialog = (target: EventTarget | null) =>
  Boolean(
    target instanceof Element &&
      (target.closest('.quick-replies-shortcut-dialog') ||
        target.closest('.text-ai-assistant-panel') ||
        (isQuickRepliesShortcutDialogOpen() && target.closest('.p-dialog-mask'))),
  )

const handleQuickRepliesOutsideClick = (event: MouseEvent) => {
  const target = event.target
  const quickRepliesContainer = getPopoverContainer(quickRepliesPanel.value)
  const quickRepliesButton = getComponentElement(quickRepliesButtonRef.value)

  if (!quickRepliesContainer) return
  if (isInsideElement(target, quickRepliesContainer)) return
  if (isInsideElement(target, quickRepliesButton)) return
  if (isInsideDialog(target)) return

  quickRepliesPanel.value?.hide()
}

const toggleEmojiPicker = (event: Event, target?: HTMLElement) => {
  emojiPanel.value?.togglePickerFromElement(target ?? getComponentElement(emojiButtonRef.value))
}

const toggleQuickReplies = (event: Event, target?: HTMLElement) => {
  quickRepliesPanel.value?.toggle(createAnchoredPopoverEvent(event, target), target)
}

const toggleFileUpload = (event: Event, target?: HTMLElement) => {
  fileUploadPanel.value?.toggle(createAnchoredPopoverEvent(event, target), target)
}

const openEmojiPickerFromShortcut = (event: Event) => {
  toggleEmojiPicker(event, getComponentElement(emojiButtonRef.value))
  focusFirstPopoverControl(emojiPanel.value)
}

const openFileUploadFromShortcut = (event: Event) => {
  toggleFileUpload(event, getComponentElement(fileUploadButtonRef.value))
  focusFirstPopoverControl(fileUploadPanel.value)
}

const openQuickRepliesFromShortcut = (event: Event) => {
  toggleQuickReplies(event, getComponentElement(quickRepliesButtonRef.value))
  focusFirstPopoverControl(quickRepliesPanel.value)
}

const toggleExpandedEditor = () => {
  isExpandedModalVisible.value = !isExpandedModalVisible.value
}

const toggleTranslation = () => {
  if (props.isTranslationDisabled) return
  emit('toggle-translation')
}

const isMessageInputShortcut = (event: KeyboardEvent, shortcutKey: string) =>
  event.altKey &&
  event.shiftKey &&
  !event.ctrlKey &&
  !event.metaKey &&
  event.key.toLowerCase() === shortcutKey

const handleGlobalShortcutKeydown = (event: KeyboardEvent) => {
  const shortcutAction = [
    {
      key: MESSAGE_INPUT_SHORTCUTS.emojis.key,
      run: () =>
        isExpandedModalVisible.value
          ? expandedMessageInputRef.value?.toggleEmojiPickerShortcut(event)
          : openEmojiPickerFromShortcut(event),
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.attachFiles.key,
      run: () =>
        isExpandedModalVisible.value
          ? expandedMessageInputRef.value?.toggleFileUploadShortcut(event)
          : openFileUploadFromShortcut(event),
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.quickReplies.key,
      run: () =>
        isExpandedModalVisible.value
          ? expandedMessageInputRef.value?.toggleQuickRepliesShortcut(event)
          : openQuickRepliesFromShortcut(event),
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.signature.key,
      run: () => emit('toggle-signature'),
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.translation.key,
      run: toggleTranslation,
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.transferChat.key,
      run: () => emit('transfer-chat'),
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.expandEditor.key,
      run: toggleExpandedEditor,
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.aiAssistant.key,
      run: () =>
        isExpandedModalVisible.value
          ? expandedMessageInputRef.value?.toggleAIMenuShortcut(event)
          : aiAssistantRef.value?.toggleAssistant(),
    },
    {
      key: MESSAGE_INPUT_SHORTCUTS.endChat.key,
      run: () => emit('end-chat'),
    },
  ].find(({ key }) => isMessageInputShortcut(event, key))

  if (!shortcutAction) return

  event.preventDefault()
  shortcutAction.run()
}

const handleEmojiSelect = (emoji: string) => {
  document.execCommand('insertText', false, emoji)
  syncTextFromEditor()
  emojiPanel.value?.hidePicker()
}

const handleQuickReply = (reply: string) => {
  recordUndoState()
  messageText.value = reply
  quickRepliesPanel.value?.hide()
  nextTick(() => {
    syncEditorFromText()
    autoResizeTextarea()
  })
}

const handleFilesSelect = (files: File[], category?: FilePreviewCategory) => {
  closeFileUpload()
  emit('files-select', files, category)
}

const handlePaste = (event: ClipboardEvent) => {
  const images = extractImageFilesFromClipboard(event)
  if (images.length > 0) {
    event.preventDefault()
    emit('files-select', images, 'image')
    return
  }

  handleEditorPaste(event)
  nextTick(() => {
    requestAnimationFrame(() => {
      autoResizeTextarea()
    })
  })
}

const handleEditorInput = () => {
  syncTextFromEditor()
  autoResizeTextarea()
}

const syncTextAndEditor = (text: string) => {
  recordUndoState()
  messageText.value = text
  nextTick(() => {
    syncEditorFromText()
    autoResizeTextarea()
    focusEditor()
  })
}

const handleAIReplace = (text: string) => {
  syncTextAndEditor(text)
}

const handleAIAppend = (text: string) => {
  syncTextAndEditor(messageText.value ? `${messageText.value}\n${text}` : text)
}

const closeFileUpload = () => {
  fileUploadPanel.value?.hide()
}

const autoResizeTextarea = () => {
  const editor = messageInput.value
  if (editor) {
    editor.style.height = 'auto'
    const maxHeight = Number.parseFloat(getComputedStyle(editor).maxHeight)
    const newHeight = Number.isFinite(maxHeight) ? Math.min(editor.scrollHeight, maxHeight) : editor.scrollHeight
    editor.style.height = `${newHeight}px`
  }
}

const resetTextareaHeight = () => {
  const editor = messageInput.value
  if (editor) {
    editor.style.height = 'auto'
  }
}

const focusInput = () => {
  focusEditor()
}

onMounted(() => {
  // Un borrador de varias lineas tiene que verse completo al volver al chat,
  // no recortado a la altura de un editor vacio.
  syncEditorFromText()
  autoResizeTextarea()
  window.addEventListener('keydown', handleGlobalShortcutKeydown)
  document.addEventListener('mousedown', handleQuickRepliesOutsideClick, true)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalShortcutKeydown)
  document.removeEventListener('mousedown', handleQuickRepliesOutsideClick, true)
})

defineExpose({
  messageText,
  messageInput,
  resetTextareaHeight,
  focusInput,
})
</script>

<style scoped>
.rich-message-editor:empty::before {
  content: attr(data-placeholder);
  color: var(--p-surface-400);
  pointer-events: none;
}

.rich-message-editor.compact {
  min-height: 1.5rem;
}

.composer-action {
  min-height: 2rem;
  min-width: 2rem;
}

.rich-message-editor :deep(code),
.rich-message-editor :deep(font[face*='monospace']) {
  border-radius: 0.25rem;
  background: rgb(0 0 0 / 0.08);
  padding: 0.0625rem 0.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
}

:global(.dark) .rich-message-editor :deep(code),
:global(.dark) .rich-message-editor :deep(font[face*='monospace']) {
  background: rgb(255 255 255 / 0.12);
}

@media (max-width: 48rem) {
  .message-input-root {
    gap: 0.625rem;
  }

  .message-composer-surface {
    gap: 0.25rem 0.375rem;
    padding: 0.375rem 0.5rem;
  }

  .rich-message-editor.compact {
    min-height: 1.75rem;
    max-height: 9rem;
    padding-inline: 0.375rem;
  }

  .composer-action {
    min-height: 1.875rem;
    min-width: 1.875rem;
  }

  .message-secondary-actions {
    align-items: flex-start;
    gap: 0.375rem 0.5rem;
    padding-inline: 0;
  }

  .message-actions-right {
    margin-left: auto;
  }

  .message-signature-hint {
    align-items: flex-start;
    padding: 0.5rem 0.625rem;
  }
}

@media (max-width: 30rem) {
  .message-composer-surface {
    align-items: center;
  }

  .rich-message-editor.compact {
    min-height: 1.75rem;
    padding-block: 0.25rem;
  }

  .message-secondary-actions {
    justify-content: flex-start;
  }

  .message-actions-right {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
